import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import {
  ArrowRightIcon,
  LikeIcon,
  LinkIcon,
  MiniProfile,
  VideoComponent,
  VideoIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { generateImageAssestUrl } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { CountryCode } from '@classity-edtech/platform-utils';
import dayjs from 'dayjs';

export interface ProductCardProps {
  product: any;
  gradeLevels: {
    secondary: { id: string; label: string }[];
    primary: { id: string; label: string }[];
  };
  onNavigateToClass: (slug: string) => void;
  onSetUserProfile: (user: any) => void;
}

export const ProductCard = ({
  product,
  gradeLevels,
  onNavigateToClass,
  onSetUserProfile,
}: ProductCardProps) => {
  const organizer = product?.productEventsData?.[0]?.attendees?.find(a => a?.organizer);

  return (
    <Box
      sx={{
        borderRadius: '16px',
        background: globalColours['$global-colour-background-elevated'],
        '&:hover': {
          boxShadow: '0 0 40px 0 rgba(69, 71, 69, 0.20)',
        },
        cursor: 'default',
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: '16px 16px 0 0',
          border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          width: '320px',
        }}
      >
        {product?.media?.contentUrl ? (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16/9',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '16px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <VideoComponent
              vidSrc={generateImageAssestUrl(product?.media?.contentUrl, 2)}
              hideControls
            />
          </Box>
        ) : (
          <Box
            component={'img'}
            src={generateImageAssestUrl(product?.image?.baseUrl, 4)}
            sx={{
              aspectRatio: '16/9',
              borderRadius: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              objectFit: 'cover',
            }}
          />
        )}

        <Box sx={{ my: 2, cursor: 'pointer' }} onClick={() => onSetUserProfile(organizer?.user)}>
          <MiniProfile
            src={generateImageAssestUrl(organizer?.user?.profile?.avatar?.contentUrl, 3)}
            name={`${organizer?.user?.profile?.firstname} ${organizer?.user?.profile?.lastname}`}
            badge={
              gradeLevels?.primary?.find(p => p?.id === product?.classroom?.gradeLevel)?.label ||
              gradeLevels?.secondary?.find(p => p?.id === product?.classroom?.gradeLevel)?.label
            }
            isBadgeActive
            countryCode={CountryCode.NG}
            followers={20}
            rating={4.9}
            followAction={() => {}}
          />
        </Box>

        <Stack onClick={() => onNavigateToClass(product?.slug)} sx={{ cursor: 'pointer' }}>
          <Typography variant='body1SemiBold'>
            {product?.title?.find(n => n.language === 'en')?.text}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product?.description?.find(n => n.language === 'en')?.text}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', px: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
          }}
        >
          <Typography variant='subtitle5'>
            {product?.productEventsData?.[0]?.startTime?.dateTime &&
              dayjs(product?.productEventsData?.[0]?.startTime?.dateTime).format('MMM')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <LikeIcon />
              <Typography variant='body3'>Save</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <LinkIcon />
              <Typography variant='body3'>Share</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', py: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pr: 1,
              mr: 1,
              borderRight: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            }}
          >
            <Typography variant='body3SemiBold'>
              {dayjs(product?.productEventsData?.[0]?.startTime?.dateTime).format('ddd')}
            </Typography>
            <Typography variant='subtitle2'>
              {dayjs(product?.productEventsData?.[0]?.startTime?.dateTime).date()}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box
              sx={{
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                color: globalColours['$global-colour-content-secondary'],
                gap: 0.5,
              }}
            >
              <Typography variant='body3'>
                {`${dayjs(product?.productEventsData?.[0]?.startTime?.dateTime).format('HH:mm')} - ${dayjs(product?.productEventsData?.[0]?.endTime?.dateTime).format('HH:mm')}`}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  height: '24px',
                }}
              >
                <VideoIcon
                  sx={{
                    width: '14px',
                    height: '14px',
                    color: globalColours['$global-colour-content-secondary'],
                  }}
                />
                <Typography variant='body3'>Online</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                alignItems: 'flex-end',
              }}
            >
              <Typography variant='body3'>
                {product?.productEventsData?.[0]?.lessons?.[0]?.classroom?.capacity
                  ? `${product?.productEventsData?.[0]?.lessons?.[0]?.classroom?.capacity - product?.productEventsData?.[0]?.lessons?.[0]?.participants?.length} seats remaining`
                  : '4 seats remaining'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                }}
              >
                {Array(4)
                  .fill('')
                  ?.map((_, i) => (
                    <Box
                      key={i}
                      component={'img'}
                      src={'https://static03.classity.co/img/Placeholder-Avatar.png'}
                      sx={{
                        aspectRatio: '1/1',
                        width: '24px',
                        height: '24px',
                        borderRadius: '100%',
                      }}
                    />
                  ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
            my: 1.5,
            cursor: 'pointer',
          }}
          onClick={() => onNavigateToClass(product?.slug)}
        >
          <Typography variant='body2SemiBold'>
            From ₦{(product.prices?.[0]?.currencies?.[0]?.amount / 24)?.toLocaleString()} per lesson
          </Typography>

          <ArrowRightIcon
            sx={{
              width: '24px',
              height: '24px',
              cursor: 'pointer',
              color: globalColours['$global-colour-interactive-control-default'],
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

ProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  gradeLevels: PropTypes.object.isRequired,
  onNavigateToClass: PropTypes.func.isRequired,
  onSetUserProfile: PropTypes.func.isRequired,
};

export default styled<any>(ProductCard)(
  ({ theme }) => `
`,
);
